import React, { useState, useRef } from 'react';
import { X, Image, Type, Palette, Upload, Send } from 'lucide-react';
import { storyService } from '../../services/api';
import api from '../../services/api';
import toast from 'react-hot-toast';

interface StoryCreatorProps {
  isOpen: boolean;
  onClose: () => void;
  onStoryCreated: () => void;
}

const StoryCreator: React.FC<StoryCreatorProps> = ({
  isOpen,
  onClose,
  onStoryCreated
}) => {
  const [storyType, setStoryType] = useState<'image' | 'video' | 'text'>('image');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState('');
  const [caption, setCaption] = useState('');
  const [backgroundColor, setBackgroundColor] = useState('#000000');
  const [textColor, setTextColor] = useState('#FFFFFF');
  const [font, setFont] = useState<'default' | 'bold' | 'italic' | 'handwriting'>('default');
  const [duration, setDuration] = useState(15);
  const [visibility, setVisibility] = useState<'public' | 'friends' | 'close-friends'>('public');
  const [loading, setLoading] = useState(false);
  const [overlayText, setOverlayText] = useState('');
  const [overlayTextColor, setOverlayTextColor] = useState('#FFFFFF');
  const [overlayFont, setOverlayFont] = useState<'default' | 'bold' | 'italic' | 'handwriting'>('default');
  const [overlayPosition, setOverlayPosition] = useState<'top' | 'center' | 'bottom'>('bottom');
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const backgroundColors = [
    '#000000', '#FFFFFF', '#FF6B6B', '#4ECDC4', '#45B7D1', 
    '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F'
  ];

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      
      // Create preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      
      // Set story type based on file type
      if (file.type.startsWith('image/')) {
        setStoryType('image');
      } else if (file.type.startsWith('video/')) {
        setStoryType('video');
      }
    }
  };

  const handleCreateStory = async () => {
    if (storyType === 'text' && !textContent.trim()) {
      toast.error('Please enter some text for your story');
      return;
    }
    
    if ((storyType === 'image' || storyType === 'video') && !selectedFile) {
      toast.error('Please select a file for your story');
      return;
    }

    setLoading(true);

    try {
      let mediaUrl = '';
      
      // Upload file if needed
      if (selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('folder', 'stories');
        formData.append('type', storyType);

        const uploadResponse = await api.post('/upload/single', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (uploadResponse.data.success) {
          mediaUrl = uploadResponse.data.data.url;
        } else {
          throw new Error('File upload failed');
        }
      }

      const storyData = {
        type: storyType,
        ...(mediaUrl && { url: mediaUrl }),
        ...(storyType === 'text' && {
          text: textContent,
          backgroundColor,
          textColor,
          font
        }),
        ...((storyType === 'image' || storyType === 'video') && overlayText.trim() && {
          text: overlayText.trim(),
          textColor: overlayTextColor,
          font: overlayFont,
          textPosition: overlayPosition
        }),
        ...((storyType === 'image' || storyType === 'video') && caption.trim() && !overlayText.trim() && {
          text: caption.trim(),
          textColor: '#FFFFFF',
          font: 'default'
        }),
        duration,
        visibility,
        allowReplies: true
      };

      console.log('Creating story with data:', storyData);
      const response = await storyService.createStory(storyData);
      console.log('Story creation response:', response);
      console.log('Response data:', response.data);
      console.log('Response success:', (response.data as any)?.success);
      
      // Check different possible response structures
      const isSuccess = (response as any)?.success || (response as any).status === 201 || (response as any).status === 200;
      
      if (isSuccess) {
        console.log('Story created successfully, calling onStoryCreated');
        toast.success('Story created successfully! 🎉');
        onStoryCreated();
        handleClose();
      } else {
        console.error('Story creation failed - no success flag');
        console.error('Full response:', JSON.stringify(response, null, 2));
        toast.error('Failed to create story');
      }
    } catch (error: any) {
      console.error('Failed to create story:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to create story');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStoryType('image');
    setSelectedFile(null);
    setPreviewUrl(null);
    setTextContent('');
    setCaption('');
    setBackgroundColor('#000000');
    setTextColor('#FFFFFF');
    setFont('default');
    setDuration(15);
    setVisibility('public');
    setOverlayText('');
    setOverlayTextColor('#FFFFFF');
    setOverlayFont('default');
    setOverlayPosition('bottom');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Create Story</h2>
          <button
            onClick={handleClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 max-h-[calc(90vh-140px)] overflow-y-auto">
          {/* Story Type Selection */}
          <div className="flex space-x-2">
            <button
              onClick={() => setStoryType('text')}
              className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                storyType === 'text'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 text-gray-700 hover:border-gray-300'
              }`}
            >
              <Type className="w-5 h-5 mx-auto mb-1" />
              <span className="text-sm font-medium">Text</span>
            </button>
            
            <button
              onClick={() => {
                setStoryType('image');
                fileInputRef.current?.click();
              }}
              className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                storyType === 'image'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 text-gray-700 hover:border-gray-300'
              }`}
            >
              <Image className="w-5 h-5 mx-auto mb-1" />
              <span className="text-sm font-medium">Photo</span>
            </button>
            
            <button
              onClick={() => {
                setStoryType('video');
                fileInputRef.current?.click();
              }}
              className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                storyType === 'video'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 text-gray-700 hover:border-gray-300'
              }`}
            >
              <Upload className="w-5 h-5 mx-auto mb-1" />
              <span className="text-sm font-medium">Video</span>
            </button>
          </div>

          {/* File Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept={storyType === 'image' ? 'image/*' : 'video/*'}
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* Preview Area */}
          <div className="w-full h-72 bg-gray-900 rounded-lg overflow-hidden relative">
            {storyType === 'text' && (
              <div
                className="w-full h-full flex items-center justify-center p-4"
                style={{ backgroundColor }}
              >
                <textarea
                  value={textContent}
                  onChange={(e) => setTextContent(e.target.value)}
                  placeholder="Type your story..."
                  className={`w-full h-full bg-transparent resize-none focus:outline-none text-center text-xl ${
                    font === 'bold' ? 'font-bold' :
                    font === 'italic' ? 'italic' :
                    font === 'handwriting' ? 'font-serif' : ''
                  }`}
                  style={{ color: textColor }}
                  maxLength={200}
                />
              </div>
            )}
            
            {storyType === 'image' && previewUrl && (
              <div className="relative w-full h-full">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="w-full h-full object-cover"
                />
                {/* Text overlay preview */}
                {overlayText.trim() && (
                  <div className={`absolute left-0 right-0 p-4 flex ${
                    overlayPosition === 'top' ? 'top-0' :
                    overlayPosition === 'center' ? 'top-1/2 -translate-y-1/2' :
                    'bottom-0'
                  }`}>
                    <p
                      className={`w-full text-center text-lg drop-shadow-lg ${
                        overlayFont === 'bold' ? 'font-bold' :
                        overlayFont === 'italic' ? 'italic' :
                        overlayFont === 'handwriting' ? 'font-serif' : ''
                      }`}
                      style={{ color: overlayTextColor }}
                    >
                      {overlayText}
                    </p>
                  </div>
                )}
                {/* Caption overlay preview (fallback if no overlay text) */}
                {!overlayText.trim() && caption.trim() && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4 pt-10">
                    <p className="text-white text-sm leading-relaxed">{caption}</p>
                  </div>
                )}
              </div>
            )}
            
            {storyType === 'video' && previewUrl && (
              <div className="relative w-full h-full">
                <video
                  src={previewUrl}
                  className="w-full h-full object-cover"
                  controls
                />
                {/* Text overlay preview */}
                {overlayText.trim() && (
                  <div className={`absolute left-0 right-0 p-4 flex pointer-events-none ${
                    overlayPosition === 'top' ? 'top-0' :
                    overlayPosition === 'center' ? 'top-1/2 -translate-y-1/2' :
                    'bottom-12'
                  }`}>
                    <p
                      className={`w-full text-center text-lg drop-shadow-lg ${
                        overlayFont === 'bold' ? 'font-bold' :
                        overlayFont === 'italic' ? 'italic' :
                        overlayFont === 'handwriting' ? 'font-serif' : ''
                      }`}
                      style={{ color: overlayTextColor }}
                    >
                      {overlayText}
                    </p>
                  </div>
                )}
                {/* Caption overlay preview (fallback if no overlay text) */}
                {!overlayText.trim() && caption.trim() && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4 pt-10 pointer-events-none">
                    <p className="text-white text-sm leading-relaxed">{caption}</p>
                  </div>
                )}
              </div>
            )}
            
            {(storyType === 'image' || storyType === 'video') && !previewUrl && (
              <div className="w-full h-full flex items-center justify-center">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex flex-col items-center space-y-2 text-gray-400 hover:text-gray-200"
                >
                  <Upload className="w-12 h-12" />
                  <span className="text-sm">
                    Click to select {storyType === 'image' ? 'photo' : 'video'}
                  </span>
                </button>
              </div>
            )}
          </div>

          {/* Text Overlay for Image/Video stories */}
          {(storyType === 'image' || storyType === 'video') && (
            <div className="space-y-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-sm font-medium text-gray-700">
                Add text to your story
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={overlayText}
                  onChange={(e) => setOverlayText(e.target.value)}
                  placeholder="Type text to overlay on your story..."
                  maxLength={100}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                  {overlayText.length}/100
                </span>
              </div>

              {overlayText.trim() && (
                <div className="space-y-3">
                  {/* Text Position */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Position</label>
                    <div className="flex space-x-2">
                      {(['top', 'center', 'bottom'] as const).map((pos) => (
                        <button
                          key={pos}
                          onClick={() => setOverlayPosition(pos)}
                          className={`flex-1 px-3 py-1.5 text-xs rounded-md border transition-colors ${
                            overlayPosition === pos
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-300 text-gray-600 hover:border-gray-400'
                          }`}
                        >
                          {pos.charAt(0).toUpperCase() + pos.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Text Color */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Text Color</label>
                    <div className="flex space-x-2">
                      {['#FFFFFF', '#000000', '#FF6B6B', '#FFEAA7', '#4ECDC4', '#45B7D1'].map((color) => (
                        <button
                          key={color}
                          onClick={() => setOverlayTextColor(color)}
                          className={`w-7 h-7 rounded-full border-2 transition-transform ${
                            overlayTextColor === color ? 'border-blue-500 scale-110' : 'border-gray-300'
                          }`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Font Style */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Font Style</label>
                    <div className="flex space-x-2">
                      {([
                        { value: 'default', label: 'Aa' },
                        { value: 'bold', label: 'Aa' },
                        { value: 'italic', label: 'Aa' },
                        { value: 'handwriting', label: 'Aa' }
                      ] as const).map((f) => (
                        <button
                          key={f.value}
                          onClick={() => setOverlayFont(f.value)}
                          className={`flex-1 px-2 py-1.5 text-sm rounded-md border transition-colors ${
                            overlayFont === f.value
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-300 text-gray-600 hover:border-gray-400'
                          } ${
                            f.value === 'bold' ? 'font-bold' :
                            f.value === 'italic' ? 'italic' :
                            f.value === 'handwriting' ? 'font-serif' : ''
                          }`}
                        >
                          {f.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Caption Input for Image/Video stories (separate from overlay text) */}
          {(storyType === 'image' || storyType === 'video') && previewUrl && !overlayText.trim() && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Or add a simple caption
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="Write a caption..."
                  maxLength={150}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                  {caption.length}/150
                </span>
              </div>
            </div>
          )}

          {/* Text Story Controls */}
          {storyType === 'text' && (
            <div className="space-y-4">
              {/* Background Colors */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Background Color
                </label>
                <div className="flex space-x-2 flex-wrap">
                  {backgroundColors.map((color) => (
                    <button
                      key={color}
                      onClick={() => setBackgroundColor(color)}
                      className={`w-8 h-8 rounded-full border-2 ${
                        backgroundColor === color ? 'border-blue-500' : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              {/* Text Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Text Color
                </label>
                <div className="flex space-x-2">
                  {['#FFFFFF', '#000000', '#FF6B6B', '#4ECDC4', '#45B7D1'].map((color) => (
                    <button
                      key={color}
                      onClick={() => setTextColor(color)}
                      className={`w-8 h-8 rounded-full border-2 ${
                        textColor === color ? 'border-blue-500' : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              {/* Font Style */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Font Style
                </label>
                <select
                  value={font}
                  onChange={(e) => setFont(e.target.value as any)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="default">Default</option>
                  <option value="bold">Bold</option>
                  <option value="italic">Italic</option>
                  <option value="handwriting">Handwriting</option>
                </select>
              </div>
            </div>
          )}

          {/* Story Settings */}
          <div className="space-y-4">
            {/* Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Duration: {duration}s
              </label>
              <input
                type="range"
                min="5"
                max="30"
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                className="w-full"
              />
            </div>

            {/* Visibility */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Who can see this?
              </label>
              <select
                value={visibility}
                onChange={(e) => setVisibility(e.target.value as any)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="public">Everyone</option>
                <option value="friends">Friends only</option>
                <option value="close-friends">Close friends</option>
              </select>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={handleCreateStory}
            disabled={loading || (storyType === 'text' && !textContent.trim())}
            className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <>
                <Send className="w-5 h-5" />
                <span>Share Story</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StoryCreator;